// entrydata / columninfo から値・列IDを大小文字を無視して解決する共通ヘルパ。
// 加工指示・品番マスタで同一実装のため共有する。
export function createEntryValueResolver(deps) {
    const {
        isEmpty,
        getColumnInfo
    } = deps;

    function parseBoolLike(value, defaultVal = false) {
        if (value === undefined || value === null) return defaultVal;
        if (typeof value === "boolean") return value;
        if (typeof value === "number") return value !== 0;
        const s = String(value).trim().toLowerCase();
        if (s === "") return defaultVal;
        if (["1", "true", "yes", "y", "on"].includes(s)) return true;
        if (["0", "false", "no", "n", "off"].includes(s)) return false;
        return defaultVal;
    }

    // "01:国内" のような表示値から先頭のコード部分だけを取り出す。
    function normalizeCodeValue(raw) {
        const s = String(raw ?? "").trim();
        if (s === "") return "";
        const m = s.match(/^(-?\d+)/);
        if (!isEmpty(m)) return m[1];
        return s;
    }

    function findEntryValue(entryObj, candidates) {
        if (isEmpty(entryObj) || !Array.isArray(candidates) || candidates.length === 0) return "";
        const keys = Object.keys(entryObj);
        for (let i = 0; i < candidates.length; i++) {
            const cand = String(candidates[i] ?? "");
            if (cand === "") continue;
            if (cand in entryObj && String(entryObj[cand] ?? "").trim() !== "") return entryObj[cand];
            const low = cand.toLowerCase();
            const byKey = keys.find(k => String(k ?? "").toLowerCase() === low);
            if (!isEmpty(byKey) && String(entryObj[byKey] ?? "").trim() !== "") return entryObj[byKey];
        }
        return "";
    }

    function findColumnInfoByCandidates(candidates, typeCandidates) {
        const columninfo = getColumnInfo();
        if (!Array.isArray(columninfo) || columninfo.length === 0) return null;
        const ids = (Array.isArray(candidates) ? candidates : [])
            .map(c => String(c ?? "").trim().toUpperCase())
            .filter(c => c !== "");
        if (ids.length === 0) return null;
        const tset = (Array.isArray(typeCandidates) ? typeCandidates : []).map(t => String(t ?? "").trim());
        const hit = columninfo.find((c) => {
            const cid = String(c.columnid ?? "").trim().toUpperCase();
            if (!ids.includes(cid)) return false;
            if (tset.length === 0) return true;
            return tset.includes(String(c.type ?? "").trim());
        });
        return isEmpty(hit) ? null : hit;
    }

    function resolveEntryColumnId(candidates, entryObj) {
        const columninfo = getColumnInfo();
        if (!Array.isArray(candidates) || candidates.length === 0) return "";
        const uppers = candidates.map(c => String(c ?? "").trim().toUpperCase()).filter(c => c !== "");
        if (uppers.length === 0) return "";
        if (Array.isArray(columninfo) && columninfo.length !== 0) {
            const hit = columninfo.find(c => uppers.includes(String(c.columnid ?? "").trim().toUpperCase()));
            if (!isEmpty(hit)) return String(hit.columnid ?? "").trim();
        }
        const keys = isEmpty(entryObj) ? [] : Object.keys(entryObj);
        for (let i = 0; i < uppers.length; i++) {
            const byKey = keys.find(k => String(k ?? "").trim().toUpperCase() === uppers[i]);
            if (!isEmpty(byKey)) return String(byKey);
        }
        return uppers[0];
    }

    // 列定義の options(配列/JSON文字列/カンマ区切り)を文字列配列に正規化する。
    function normalizeColOptionsToStrings(options) {
        const out = [];
        const pushVal = (v) => {
            const s = String(v ?? "").trim();
            if (s === "") return;
            if (out.indexOf(s) !== -1) return;
            out.push(s);
        };
        if (Array.isArray(options)) {
            options.forEach((o) => {
                if (o === null || o === undefined) return;
                if (typeof o === "string" || typeof o === "number") {
                    pushVal(o);
                    return;
                }
                pushVal(o.value ?? o.label ?? o.name ?? o.text ?? o.code ?? o.cd ?? "");
            });
            return out;
        }
        if (typeof options === "string") {
            const raw = options.trim();
            if (raw === "") return out;
            try {
                const parsed = JSON.parse(raw);
                return normalizeColOptionsToStrings(parsed);
            } catch (e) {
                raw.split(",").forEach(v => pushVal(v));
                return out;
            }
        }
        return out;
    }

    return {
        parseBoolLike,
        normalizeCodeValue,
        findEntryValue,
        findColumnInfoByCandidates,
        resolveEntryColumnId,
        normalizeColOptionsToStrings
    };
}
